import { ValidationButton } from 'commons/components/ValidationButton'
import { WalletPaymentStage } from 'commons/components/modal/WalletPaymentStage'
import { useFormContext } from 'commons/config/form'
import { isXpayType } from 'commons/constants/paymentTypes'
import type { PaymentSubmitRequest } from 'commons/types/paymentTypes'
import { useCallback, useEffect, useRef } from 'react'

type PaymentSubmitProps = {
	request: PaymentSubmitRequest | null
	onReset: () => void
	onPaymentCompleted?: () => void
	isFromMoneyPot?: boolean
}

export const PaymentSubmit = ({
	request,
	onReset,
	onPaymentCompleted,
	isFromMoneyPot
}: PaymentSubmitProps) => {
	const form = useFormContext()
	const processingRef = useRef(false)
	const invalidateRef = useRef<(() => void) | null>(null)
	const onProcessingChange = useCallback((processing: boolean) => {
		processingRef.current = processing
		if (!processing) invalidateRef.current?.()
	}, [])

	useEffect(() => {
		if (!request || !isXpayType(request.paymentType)) return
		const invalidate = () => {
			// Never discard a token submission that may already be charging the payer.
			if (!processingRef.current && JSON.stringify(form.store.state.values) !== request.formValues)
				onReset()
		}
		invalidateRef.current = invalidate
		invalidate()
		const unsubscribe = form.store.subscribe(invalidate)
		return () => {
			invalidateRef.current = null
			unsubscribe()
		}
	}, [form, request, onReset])

	if (request && isXpayType(request.paymentType)) {
		return (
			<WalletPaymentStage
				key={request.requestPubUuid}
				requestPubUuid={request.requestPubUuid}
				paymentType={request.paymentType}
				amount={request.amount}
				onClose={onReset}
				onPaymentCompleted={onPaymentCompleted}
				onProcessingChange={onProcessingChange}
			/>
		)
	}
	return <ValidationButton isFromMoneyPot={isFromMoneyPot} isPaymentPending={request !== null} />
}
